import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {FetchDashboardFilterModel} from '../model/fetch-dashboard-filter.model';
import {FetchClinicFilterModel} from '../model/fetch-clinic-filter.model';
import {FetchStatusFilterModel} from '../model/fetch-status-filter.model';
import {FetchClinicListModel} from '../model/fetch-clinic-list.model';
import {FetchGroupsListModel} from '../model/fetch-groups-list.model';
import {
  FetchParticipantsAndCaregiversListModel
} from '../model/fetch-participants-and-caregivers-list.model';
import {
  FetchParticipantsWeightListModel
} from '../model/fetch-participants-weight-list.model';
import {
  FetchParticipantsBehaviourListModel
} from '../model/fetch-participants-behaviour-list.model';
import {
  FetchMetricOptionListModel
} from '../model/fetch-metric-option-list.model';
import {
  FetchMeasurementFilterModel
} from '../model/fetch-measurement-filter.model';
import {v1URL} from '../../../shared/constants/urls.constants';
import {CommonService} from '../../../shared/services/common.service';
import {AddMetricOptionModel} from '../model/add-metric-option.model';
import {UpdateMetricOptionModel} from '../model/update-metric-option.model';
import {
  FetchGroupTypeFilterModel
} from "../model/fetch-group-type-filter.model";
import {FetchOrgTypeFilterModel} from '../model/fetch-org-type-filter.model';
import {
  FetchClinicStatusFilterModel
} from '../model/fetch-clinic-status-filter.model';
import {FetchGroupFilterModel} from '../model/fetch-group-filter.model';
import {
  FetchUserStatusFilterModel
} from '../model/fetch-user-status-filter.model';

@Injectable({
  providedIn: 'root',
})
export class ClientsListService {
  constructor(private commonServices: CommonService) {
  }

  fetchClientsList(): Observable<any['response']> {
    return of({
      success: true,
      code: 'Dummy Code  ABCD1234',
      message: 'Weeks List Fetched Successfully',
      data: [
        {
          id: 1,
          name: 'Client 1',
          admins: [
            {
              id: 1,
              name: 'Admin 1'
            },
            {
              id: 2,
              name: 'Admin 2'
            },
            {
              id: 3,
              name: 'Admin 3'
            }
          ]
        },
      ],
    });
  }

  fetchDashboardFilter(): Observable<FetchDashboardFilterModel['response']> {
    return of({
      success: true,
      code: 'Dummy Code ABCD1234',
      message: 'Dummy Message',
      data: {
        list: [
          {
            key: 1,
            value: 'Organization Information'
          },
          {

            key: 2,
            value: 'Group Information'
          },
          {
            key: 3,
            value: 'Participant/Adult Information'
          },
          {
            key: 4,
            value: 'Participant Weight'
          },
          {
            key: 5,
            value: 'Participant Behavior'
          },
          {
            key: 6,
            value: 'Measurement Information'
          }
        ],
        total: null,
        pageNumber: 1,
        limit: 10,
        hasNext: false,
        hasPrev: false,
        loading: false
      }
    })
  }

  fetchClinicFilter(payload: FetchClinicFilterModel['request']): Observable<FetchClinicFilterModel['response']> {
    const urlConfig = v1URL.filters.clients
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchStatusFilter(): Observable<FetchStatusFilterModel['response']> {
    const urlConfig = v1URL.filters.groupStatus
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url);
  }

  fetchGroupTypeFilter(): Observable<FetchGroupTypeFilterModel['response']> {
    const urlConfig = v1URL.filters.groupType
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url);
  }

  fetchClinicList(payload: FetchClinicListModel['request']): Observable<FetchClinicListModel['response']> {
    const urlConfig = v1URL.client.fetchAll
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchGroupsList(payload: FetchGroupsListModel['request']): Observable<FetchGroupsListModel['response']> {
    const urlConfig = v1URL.groups.fetchAll
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchParticipantsAndCaregiversList(payload: FetchParticipantsAndCaregiversListModel['request']): Observable<FetchParticipantsAndCaregiversListModel['response']> {
    const urlConfig = v1URL.dashboard.participantCaregiver
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchParticipantsWeightList(payload: FetchParticipantsWeightListModel['request']): Observable<FetchParticipantsWeightListModel['response']> {
    const urlConfig = v1URL.dashboard.participantWeightInformation
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchParticipantsBehaviourList(payload: FetchParticipantsBehaviourListModel['request']): Observable<FetchParticipantsBehaviourListModel['response']> {
    const urlConfig = v1URL.dashboard.participantBehaviourInformation
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchMetricOptionList(payload: FetchMetricOptionListModel['request']): Observable<FetchMetricOptionListModel['response']> {
    const params = {
      manage: true,
      pageNumber: payload.pageNumber,
      limit: payload.limit,
      isActive: payload.isActive,
      searchText: payload.searchText,
      sortOrder: payload.sortOrder,
      sortColumn: payload.sortColumn,
    }
    const urlConfig = v1URL.measurements.options.fetchAll
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(params));
  }

  addMetricOption(payload: AddMetricOptionModel['request']): Observable<AddMetricOptionModel['response']> {
    const urlConfig = v1URL.measurements.options.add
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(), payload);
  }

  updateMetricOption(payload: UpdateMetricOptionModel['request']): Observable<UpdateMetricOptionModel['response']> {
    const params = {
      metricId: payload.metricId,
    }
    const body = {
      name: payload.name,
      code: payload.code,
      type: payload.type,
      isActive: payload.isActive,
    }
    const urlConfig = v1URL.measurements.options.update
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(params), body);
  }

  fetchMeasurementFilter(): Observable<FetchMeasurementFilterModel['response']> {
    return of({
      success: true,
      code: 'Dummy Code ABCD1234',
      message: 'Dummy Message',
      data: {
        list: [
          {
            key: 1,
            value: 'All'
          }
        ],
        total: null,
        pageNumber: 1,
        limit: 10,
        hasNext: false,
        hasPrev: false,
        loading: false
      }
    })
  }

  fetchOrgTypeFilterList(): Observable<FetchOrgTypeFilterModel['response']> {
    const urlConfig = v1URL.filters.orgType
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url);
  }

  fetchClinicStatusFilterList(): Observable<FetchClinicStatusFilterModel['response']> {
    const urlConfig = v1URL.filters.clientStatus
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url);
  }

  fetchGroupListFilter(payload: FetchGroupFilterModel['request']): Observable<FetchGroupFilterModel['response']> {
    const urlConfig = v1URL.filters.groups
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url(payload));
  }

  fetchUserStatusListFilter(): Observable<FetchUserStatusFilterModel['response']> {
    const urlConfig = v1URL.filters['user-status']
    return this.commonServices.callAPI(urlConfig.method, urlConfig.url);
  }
}
